import { ExistingItemHandling, type BasketItem } from '@scayle/storefront-nuxt'
import { useFreeGiftPromotion } from './useFreeGiftPromotion'
import { isAutomaticDiscountType } from '../utils/promotion'

export default function useAutomaticDiscountPromotion() {
  const basket = useAgsBasket()
  const log = useLog('useAutmaticDiscountPromotion')
  const { isGiftProduct } = useFreeGiftPromotion(false)

  /**
   * Tries to apply the promotion to all valid basket items
   */
  const tryApply = async (promotion: Promotion) => {
    if (!isAutomaticDiscountType(promotion)) return { success: false }

    if (!basket.data.value?.items?.length) {
      return { success: false, reason: 'No items in basket' }
    }

    const items = getValidBasketItems(promotion)
    if (!items.length) {
      return { success: false, reason: 'No valid items' }
    }

    log.debug('apply-automatic-discount', { promotion, items })
    return await applyToBasketItems(promotion, items)
  }

  /**
   * Validates the item-conditions for each basket item and returns the valid ones
   */
  const getValidBasketItems = (promotion: Promotion) => {
    log.debug('get-valid-basket-items', {
      promotion,
      items: basket.data.value?.items,
    })

    return (basket.data.value?.items || []).filter((item) => {
      if (item.promotion && item.promotion.priority <= promotion.priority) {
        log.debug('skip-same-or-higher-priority', {
          item,
          promotion,
        })
        return false
      }
      if (isGiftProduct(item)) {
        log.debug('skip-free-gift', { item })
        return false
      }
      return true
    })
  }

  /**
   * Applies the promotion to all _valid_ basket items
   */
  const applyToBasketItems = async (
    promotion: Promotion,
    items: BasketItem[],
  ) => {
    let successCounter = 0

    for (const item of items) {
      const update = {
        variantId: item.variant.id,
        quantity: item.quantity,
        promotionId: promotion?.id,
        customData: item.customData as Record<string, any>,
        displayData: item.displayData,
        existingItemHandling: ExistingItemHandling.REPLACE_EXISTING,
      }

      try {
        await basket.addItem(update)
        log.debug('basket-updated-item', { item, promotion })
        successCounter++
      } catch (error) {
        log.debug('basket-updated-failed', { item, promotion, error })
      }
    }

    return {
      success: successCounter > 0,
      reason:
        successCounter > 0
          ? 'Successfully applied promotion to ' + successCounter + ' items'
          : 'Error applying discount to items',
    }
  }

  return {
    tryApply,
  }
}
